import { useMemo, useState, type ReactElement } from "react"
import { faEye, faEyeSlash, faPen, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"
import { Modal, Pagination, Loading, Button as WfpButton, Tag } from "@wfp/ui"
import { AutoResizer, Column, type ColumnShape } from "react-base-table"
import { MARKET_MODULE } from "constants/constants"
import { iconAddOutline, iconSubtractOutline } from "@wfp/icons"

import {
  useDeleteSeriesMutation,
  useDeleteMultipleSeriesMutation,
  useHideMultipleSeriesMutation,
  useLazyGetSeriesCombinationQuery,
} from "services/core/series"
import { SeriesCombination, SeriesPageFilters } from "interfaces/dataInterfaces"
import Button from "components/Shared/Button/Button"
import CountrySelector from "components/Shared/HeaderFilters/AssociatedCountriesSelector"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import SelectableTable from "components/Shared/Table/SelectableTable"
import useCommon from "hooks/useCommon"
import FilterTag from "components/Shared/Common/FilterTag"
import AddSeries from "./AddSeries"
import AdvancedFilters from "./AdvancedFilters"

const permissionsProps = {
  moduleId: MARKET_MODULE,
  subModule: "ccpucs",
}

const initialFilters: SeriesPageFilters = {
  adm0Code: 0,
  market: 0,
  commodity: 0,
  unit: 0,
  currency: 0,
  priceType: 0,
  seriesCombinationVisibility: null,
}

const Series = (): ReactElement => {
  const { showSnackBar } = useCommon()
  const [filters, setFilters] = useState<SeriesPageFilters>(initialFilters)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(50)
  const [showAddModal, setShowAddModal] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showVisiblityModal, setShowVisiblityModal] = useState<"show" | "hide" | null>(null)
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [actionItem, setActionItem] = useState<{
    item: SeriesCombination
    action: "edit" | "delete"
  } | null>(null)
  const [filtersExpanded, setFiltersExpanded] = useState(true)
  const [filterTags, setFilterTags] = useState<{ key: string; label: string; value: string }[]>([])

  const [deleteSeriesId] = useDeleteSeriesMutation()
  const [deleteMultipleSeries] = useDeleteMultipleSeriesMutation()
  const [hideMultipleSeries] = useHideMultipleSeriesMutation()
  const [triggerGetSeries, { data: seriesData, isFetching: isLoadingSeries }] = useLazyGetSeriesCombinationQuery()

  const getAllowedAction = (
    gridSelectedRows: number[],
    gridSeriesData: SeriesCombination[],
  ): "hide" | "show" | "both" => {
    const rowsData = gridSeriesData.filter(x => gridSelectedRows.includes(x.seriesCombinationID))
    if (rowsData.every(x => x.seriesCombinationVisibility === true)) return "hide"
    if (rowsData.every(x => x.seriesCombinationVisibility === false)) return "show"
    return "both"
  }

  const allowedAction = useMemo(
    () => getAllowedAction(selectedRows, seriesData?.items ?? []),
    [selectedRows, seriesData?.items],
  )

  const handleDelete = async (id: number) => {
    await deleteSeriesId(id)
    setActionItem(null)
  }

  const closeModal = () => {
    setActionItem(null)
    setShowAddModal(false)
  }

  const handleDeleteMultiple = async (): Promise<void> => {
    try {
      await deleteMultipleSeries(selectedRows).unwrap()
      setSelectedRows([])
      setShowDeleteModal(false)
      showSnackBar("Selected records deleted successfully", "success")
    } catch (error) {
      showSnackBar("Error deleting selected records", "error")
    }
  }

  const handleHideMultiple = async (isVisibleFlag: boolean): Promise<void> => {
    try {
      await hideMultipleSeries({ ids: selectedRows, isVisible: isVisibleFlag }).unwrap()
      setSelectedRows([])
      setShowVisiblityModal(null)
      showSnackBar("Selected records hidden successfully", "success")
    } catch (error) {
      showSnackBar("Error hiding selected records", "error")
    }
  }

  const applyFilters = (pageFilter?: SeriesPageFilters): void => {
    const newFilters: any = {
      admCode: pageFilter?.adm0Code ?? filters.adm0Code,
      market: pageFilter?.market ?? filters.market,
      comodity: pageFilter?.commodity ?? filters.commodity,
      commodityUnitId: pageFilter?.unit ?? filters.unit,
      currencyId: pageFilter?.currency ?? filters.currency,
      priceTypeId: pageFilter?.priceType ?? filters.priceType,
      seriesCombinationVisibility: pageFilter?.seriesCombinationVisibility ?? filters.seriesCombinationVisibility ?? "",
      skip: pageNumber === 1 ? 0 : (pageNumber - 1) * pageSize,
      take: pageSize,
    }
    setSelectedRows([])
    setPageNumber(1)
    triggerGetSeries(newFilters)
  }

  const clearFilter = key => {
    const cleared = { ...filters }
    if (key === "seriesCombinationVisibility") cleared.seriesCombinationVisibility = null
    else (cleared as any)[key] = 0
    setFilters(cleared)
    setFilterTags(prev => prev.filter(t => t.key !== key))
    applyFilters(cleared)
  }

  const columns: Array<ColumnShape<SeriesCombination>> = [
    {
      key: "seriesCombinationID",
      dataKey: "seriesCombinationID",
      title: "ID",
      width: 200,
      maxWidth: 300,
    },
    {
      key: "marketName",
      dataKey: "marketName",
      title: "Market",
      width: 400,
      maxWidth: 450,
    },
    {
      key: "commodityName",
      dataKey: "commodityName",
      title: "Commodity",
      width: 400,
      maxWidth: 450,
    },
    {
      key: "priceTypeName",
      dataKey: "priceTypeName",
      title: "Price Type",
      width: 200,
      maxWidth: 300,
    },
    {
      key: "commodityUnitName",
      dataKey: "commodityUnitName",
      title: "Unit",
      width: 200,
      maxWidth: 300,
    },
    {
      key: "currencyName",
      dataKey: "currencyName",
      title: "Currency",
      width: 200,
      maxWidth: 300,
    },
    {
      key: "seriesCombinationVisibility",
      dataKey: "seriesCombinationVisibility",
      title: "Visibility",
      width: 240,
      maxWidth: 300,
      cellRenderer: ({ rowData }) =>
        rowData.seriesCombinationVisibility ? (
          <Tag type="success" className="font-bold">
            Public
          </Tag>
        ) : (
          <Tag type="warning" className="font-bold">
            Internal Use Only
          </Tag>
        ),
    },
    {
      key: "action",
      title: "Action",
      width: 600,
      align: Column.Alignment.CENTER,
      cellRenderer: ({ rowData }) => (
        <PermissionWrapper {...permissionsProps} feature="edit">
          <>
            <Button
              action={() =>
                setActionItem({
                  item: {
                    ...rowData,
                  },
                  action: "edit",
                })
              }
              label="Edit"
              className="btn-full blue action-btn"
              icon={faPen}
            />

            <Button
              action={() =>
                setActionItem({
                  item: {
                    ...rowData,
                  },
                  action: "delete",
                })
              }
              label="Delete"
              className="btn-full danger action-btn"
              style={{ marginLeft: "10px" }}
              icon={faTrash}
            />
          </>
        </PermissionWrapper>
      ),
    },
  ]

  return (
    <>
      <HeaderFilters
        leftWidth="w-9/12"
        left={
          <>
            <CountrySelector
              allowAll={false}
              className="w-52"
              onSelect={e => {
                const newfilters = e?.value
                  ? { ...filters, adm0Code: e.value, market: 0, commodity: 0 }
                  : { ...initialFilters }
                setFilters(newfilters)
                applyFilters(newfilters)
              }}
            />
            <WfpButton
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              kind="secondary"
              icon={filtersExpanded ? iconSubtractOutline : iconAddOutline}
              className="ml-2"
              iconDescription="Expand or collapse filters"
            >
              {filtersExpanded ? "Hide Filters" : "Advanced Filters"}
            </WfpButton>
          </>
        }
        right={
          <PermissionWrapper {...permissionsProps} feature="addNew">
            <Button action={() => setShowAddModal(true)} label="Add series" className="btn-full green" icon={faPlus} />
          </PermissionWrapper>
        }
      />
      {filtersExpanded && (
        <AdvancedFilters
          onApply={applyFilters}
          onClear={() => {
            setFilters({ ...initialFilters })
            applyFilters({ ...initialFilters })
            setFiltersExpanded(false)
            setFilterTags([])
          }}
          filters={filters}
          setFilters={setFilters}
          setFilterTags={setFilterTags}
        />
      )}

      <div className="flex flex-wrap gap-2 mt-2">
        {filterTags.map(tag => (
          <FilterTag key={tag.key} label={tag.label} value={tag.value} onClear={() => clearFilter(tag.key)} />
        ))}
      </div>
      <div className="mt-2">
        {!!selectedRows.length && (
          <div className="flex items-center gap-3">
            <Button
              action={() => setShowDeleteModal(true)}
              label="Delete selected"
              className="btn-full danger action-btn"
              icon={faTrash}
            />
            <PermissionWrapper {...permissionsProps} feature="edit">
              <div className="flex items-center gap-3">
                {allowedAction !== "hide" && (
                  <Button
                    action={() => setShowVisiblityModal("show")}
                    label="Set to Public"
                    className="btn-full blue action-btn"
                    icon={faEye}
                  />
                )}
                {allowedAction !== "show" && (
                  <Button
                    action={() => setShowVisiblityModal("hide")}
                    label="Set to Internal Use"
                    className="btn-full blue action-btn"
                    icon={faEyeSlash}
                  />
                )}
              </div>
            </PermissionWrapper>
          </div>
        )}
      </div>

      <div className="mt-1" style={{ height: "100%", minHeight: "60vh" }}>
        {isLoadingSeries && <Loading small />}
        <AutoResizer>
          {({ width, height }) => (
            <SelectableTable
              data={filters.adm0Code ? seriesData?.items : []}
              width={width}
              height={height}
              rowKey="seriesCombinationID"
              className="custom-table"
              rowHeight={38}
              headerHeight={45}
              columns={columns}
              selectedRows={selectedRows}
              onSelectedRowsChange={rows => setSelectedRows(rows)}
            />
          )}
        </AutoResizer>
      </div>

      {(showAddModal || actionItem?.action === "edit") && (
        <AddSeries
          actionItem={actionItem}
          adm0Code={filters.adm0Code}
          onClose={closeModal}
          showNewUserModal={showAddModal}
        />
      )}

      <Modal
        open={actionItem?.action === "delete"}
        modalHeading="Confirm Delete"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestSubmit={() => handleDelete(actionItem!.item.seriesCombinationID)}
        onRequestClose={() => setActionItem(null)}
      >
        Are you sure you want to delete the selected record?
      </Modal>

      <Modal
        open={showDeleteModal}
        modalHeading="Confirm Delete"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleDeleteMultiple}
        onRequestClose={() => setShowDeleteModal(false)}
      >
        Are you sure you want to delete the selected records?
      </Modal>

      <Modal
        open={showVisiblityModal === "show"}
        modalHeading="Confirm visibility"
        primaryButtonText="Set Visible"
        secondaryButtonText="Cancel"
        onRequestSubmit={() => handleHideMultiple(true)}
        onRequestClose={() => setShowVisiblityModal(null)}
      >
        Are you sure you want to show the selected records?
      </Modal>

      <Modal
        open={showVisiblityModal === "hide"}
        modalHeading="Confirm visibility"
        primaryButtonText="Set Hidden"
        secondaryButtonText="Cancel"
        onRequestSubmit={() => handleHideMultiple(false)}
        onRequestClose={() => setShowVisiblityModal(null)}
      >
        Are you sure you want to hide the selected records?
      </Modal>

      <Pagination
        totalItems={seriesData?.count ?? 0}
        pageSize={pageSize}
        pageSizes={[50, 100, 500]}
        page={pageNumber}
        onChange={event => {
          setPageNumber((event as any).page)
          setPageSize((event as any).pageSize)
        }}
      />
    </>
  )
}

export default Series
